﻿import React, { useState, useEffect } from 'react';
import { Intent, Icon } from '@blueprintjs/core';
import { Breadcrumbs } from 'components/navigation';
import { NotificationToaster, AlertUnsavedData } from "components/notifications";
import { LinkButton, Button } from "components/buttons";
import { ShowHide } from "components/layout";
import { Wizard } from "components/wizard";
import { PolicySettings } from 'components/settings';
import { UserAccess, UserHasAccess } from 'components/user-access';
import { EmployeeService } from "services";
import { PageRestricted } from 'pages/errors/page-restricted';
import { useDirtyData } from "hooks/useDirtyData";
import { PolicyDocumentSettings } from '../../../components/settings/PolicyDocumentSettings';
import moment from 'moment';
import { PolicyDetails } from 'components/settings/PolicyDetails';
import { PolicyAdditionalSettings } from 'components/settings/PolicyAdditionalSettings';
import { useNavigate } from 'react-router-dom';


export function PolicyCreate(props) {
    var navigate = useNavigate();

    const policyModel = {
        name: "",
        startDate: moment().format('YYYY-MM-DD'),
        endDate: "",
        employeeCategorys: [],
        employeeTypes: [],
        locations: [],
        resendSchedule: {
            reoccurring: false
        },
        resendPeriodId: null,
        useAllLocations: true,
        policyDocuments: [],
        sendDebriefAlert: true,
        debriefAlertDelayDays: 0
    };

    const [originalPolicySettings, setOriginalPolicySettings] = useState(policyModel);
    const [policySettingValid, setPolicySettingValid] = useState(true);
    const [policyCreated, setPolicyCreated] = useState(false);
    const [policySettings, setPolicySettings] = useState(originalPolicySettings);
    const isPolicyDataDirty = useDirtyData(originalPolicySettings, policySettings);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const requiredAction = "Documents:Manage";
    const canAccessPage = UserHasAccess(requiredAction);

    const [wizardStep, setWizardStep] = useState(0);
    const [wizardValid, setWizardValid] = useState(false);
    useEffect(initialLoad, []);

    function initialLoad() {

        if (!canAccessPage) {
            return;
        }

        setLoading(false);
    }

    function onPolicySettingValidationChange(isValid) {
        setPolicySettingValid(isValid);
    }

    function onPolicySettingsChange(item) {
        setPolicySettings({
            ...policySettings,
            ...item
        })
    }

    function savePolicy() {
        setSaving(true);

        if (!policySettingValid) {
            NotificationToaster.show(Intent.WARNING, "There are errors on the page that need your attention");
            setSaving(false);
            return;
        }

        EmployeeService.createPolicy(policySettings).then(
            (result) => {
                setPolicyCreated(true);
                setSaving(false);
                setOriginalPolicySettings(policyModel);
                NotificationToaster.show(Intent.SUCCESS, "Policy created successfully.");
                navigate(`/policy-management/policies/${result.data}`);
            },
            () => {
                setSaving(false);
                NotificationToaster.show(Intent.DANGER, "Could not create the policy. Please try again.");
            }
        );
    }

    function resetComponent() {

        setPolicySettings(policyModel);
        initialLoad();
        setSaving(false);
        setPolicyCreated(false);
        setWizardStep(0);
    }

    function onWizardChange(step) {
        setWizardStep(step);
    }

    useEffect(setIfWizardValid, [wizardStep, policySettingValid]);

    function setIfWizardValid() {
        setWizardValid(policySettingValid);
    }

    return (

        <UserAccess perform={requiredAction}
            yes={() => (
                <div className="row">

                    <Breadcrumbs items={props.breadcrumbs} />

                    <h1>New Policy</h1>

                    <ShowHide
                        evaluator={!policyCreated}
                        show={(
                            <div>
                                <Wizard onStepChange={onWizardChange} onFinish={savePolicy} canProceed={wizardValid} disabled={saving}>

                                    <div>
                                        <h1 id="new-policy-details">Details</h1>
                                        <PolicyDetails
                                            saving={saving}
                                            policy={policySettings}
                                            onSettingsUpdate={onPolicySettingsChange}
                                            onValidationUpdate={onPolicySettingValidationChange}
                                            loading={loading}
                                        />
                                    </div>
                                    <div>
                                        <h1 id="new-policy-documents">What documents would you like to add to this policy?</h1>
                                        <PolicyDocumentSettings
                                            disabled={saving}
                                            documents={policySettings}
                                            onSettingsUpdate={onPolicySettingsChange}
                                            onValidationUpdate={onPolicySettingValidationChange}
                                            loading={loading}
                                            isDataDirty={policySettings.policyDocuments.length > 0}
                                        />
                                    </div>
                                    <div>
                                        <h1 id="new-policy-settings">Policy Settings</h1>
                                        <PolicySettings
                                            saving={saving}
                                            policy={policySettings}
                                            onSettingsUpdate={onPolicySettingsChange}
                                            onValidationUpdate={onPolicySettingValidationChange}
                                            loading={loading}
                                        />
                                    </div>
                                    <div>
                                        <h1 id="new-policy-additional-settings">Additional Settings</h1>
                                        <PolicyAdditionalSettings
                                            saving={saving}
                                            policy={policySettings}
                                            onSettingsUpdate={onPolicySettingsChange}
                                            onValidationUpdate={onPolicySettingValidationChange}
                                            loading={loading}
                                        />
                                    </div>
                                </Wizard>
                                <AlertUnsavedData
                                    isDirty={isPolicyDataDirty}>
                                </AlertUnsavedData>
                            </div>
                        )}
                        hide={(
                            <div>
                                <div className="spacer-bottom" id="create-success-icon">
                                    <Icon icon="tick-circle" size={40} intent={Intent.SUCCESS} />
                                </div>
                                <h2 id="create-success-name">The policy {policySettings.name} has been added</h2>
                                <div className="spacer-bottom">
                                    <p>You can now go back to Policy Management or add another policy.</p>
                                </div>
                                <div className="button-row" id="create-success-buttons">
                                    <LinkButton intent="primary" text="Policies" id="listing-policy" href="/policy-management/policies" />
                                    <Button intent="primary" text="Add new policy" id="new-policy" onClick={resetComponent} />
                                </div>
                            </div>
                        )}
                    ></ShowHide>

                </div>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}